import React, { useEffect, useCallback, useState } from 'react';
import { Tabs, theme, Result } from 'antd';
import { ApiOutlined, MonitorOutlined, UserOutlined, SecurityScanOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';

import WorkerManagement from '../AdminSettings/components/WorkerManagement';
import QueueMonitoring from '../AdminSettings/components/QueueMonitoring';
import TrelloSettings from '../AdminSettings/components/TrelloSettings';
import UserManagement from '../AdminSettings/components/UserManagement';
import RolesAndPermissions from '../AdminSettings/components/RolesAndPermissions';
import { fetchWorkers, fetchUsers, fetchRoles } from '../AdminSettings/actions';
import '../AdminSettings/AdminSettings.css';

const { TabPane } = Tabs;

const SystemSettings = () => {
    const dispatch = useDispatch();
    const { workers, users, roles, loading } = useSelector(state => state.adminSettings);
    const { user } = useSelector(state => state.auth);

    const [activeKey, setActiveKey] = useState('workers');
    const [loadedTabs, setLoadedTabs] = useState(new Set(['workers']));

    const {
        token: { borderRadiusLG },
    } = theme.useToken();

    const isSystemAdmin = user?.userRole?.name === 'Admin' || user?.userRole?.name === 'Super Admin' || user?.role === 'Admin';

    // Load initial tab data on mount
    useEffect(() => {
        dispatch(fetchWorkers());
    }, [dispatch]);

    const fetchTabData = useCallback(async (tabKey) => {
        if (loadedTabs.has(tabKey)) return;

        try {
            switch (tabKey) {
                case 'workers':
                    await dispatch(fetchWorkers());
                    break;
                case 'users':
                    await dispatch(fetchUsers());
                    await dispatch(fetchRoles());
                    break;
                case 'roles':
                    await dispatch(fetchRoles());
                    break;
                case 'queue':
                case 'trello':
                    // Components handle their own data fetching
                    break;
                default:
                    break;
            }

            setLoadedTabs(prev => new Set([...prev, tabKey]));
        } catch (error) {
            console.error(`Error loading data for tab ${tabKey}:`, error);
        }
    }, [dispatch, loadedTabs]);

    const handleTabChange = useCallback((key) => {
        setActiveKey(key);
        fetchTabData(key);
    }, [fetchTabData]);

    if (!isSystemAdmin) {
        return (
            <div style={{ margin: '24px 16px' }}>
                <Result
                    status="403"
                    title="403"
                    subTitle="Xin lỗi, bạn không có quyền truy cập vào Cài đặt hệ thống."
                />
            </div>
        );
    }

    return (
        <div
            style={{
                margin: '24px 16px',
                borderRadius: borderRadiusLG,
                minHeight: 280,
            }}
        >
            <Tabs
                activeKey={activeKey}
                onChange={handleTabChange}
                type="card"
                tabBarGutter={5}
                className="admin-settings-tabs"
            >
                <TabPane
                    tab={<span><ApiOutlined /> Worker Management</span>}
                    key="workers"
                >
                    <WorkerManagement
                        workers={workers}
                        loading={loading}
                    />
                </TabPane>

                <TabPane
                    tab={<span><UserOutlined /> User Management</span>}
                    key="users"
                >
                    <UserManagement
                        users={users}
                        roles={roles}
                        loading={loading}
                    />
                </TabPane>

                <TabPane
                    tab={<span><SecurityScanOutlined /> Roles & Permissions</span>}
                    key="roles"
                >
                    <RolesAndPermissions
                        roles={roles}
                        loading={loading}
                    />
                </TabPane>

                <TabPane
                    tab={<span><MonitorOutlined /> Queue Monitoring</span>}
                    key="queue"
                >
                    <QueueMonitoring />
                </TabPane>

                <TabPane
                    tab={<span><ApiOutlined /> Trello Settings</span>}
                    key="trello"
                >
                    <TrelloSettings />
                </TabPane>
            </Tabs>
        </div>
    );
};

export default SystemSettings;
